/**
 * Store pour les tâches affectées à l'utilisateur connecté
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';

export const useAuthRunningTaskStore = defineStore('authRunningTask', (() => {

    // State des tâches chargées
    const runningTasks = ref([]);

    // State des tâches à venir
    const incomingRunningTasks = ref(null);

    // State de la liste des tâches (total, tasks)
    const runningTaskList = ref({});

    /**
     * Retourne la liste des tâches
     * @return array
     */
    const getRunningTaskList = computed(() => runningTaskList.value || {});

    /**
     * Retourne la tâche dont l'identifiant public est en paramètre
     * @param int publicId
     * @return object|null
     */
    const getRunningTaskByPublicId = computed(() => {
        return ((publicId) => {
            const index = runningTasks.value.findIndex((taskFromState) => taskFromState.publicId === publicId);
            if(index === -1) {
                return null;
            }
            return runningTasks.value[index];
        });
    });

    /**
     * Retourne la liste des tâches à venir
     * @return array
     */
    const getIncomingRunningTasks = computed(() => {
        return (incomingRunningTasks.value || []);
    });

    /**
     * Chargement de la liste des tâches à venir
     * @param int number Nombre de tâches à charger
     * @return Promise
     */
    const loadIncomingRunningTasks = ((number) => {

        const incomingTasks = getIncomingRunningTasks.value;
        if(incomingTasks.length > 0) {
            return Promise.resolve();
        }

        const path = '/api/me/tasks/running/incoming';

        return axios.get(path, {
            params: {
                limit: number,
                sort: {
                    order: 'planned_at',
                    direction: 'asc'
                }
            }
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.tasks) {
                updateIncomingRunningTasks(response.data.tasks);
                return Promise.resolve();
            }
            return Promise.reject();
        })
    });

    /**
     * Chargement de la liste des tâches
     * @param object params Paramètres pour la requête Ajax
     * @return Promise
     */
    const loadRunningTaskList = ((params) => {

        const path = '/api/me/tasks/running';

        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {

                if(params.offset === 0) {
                    resetRunningTaskList();
                }

                updateRunningTaskList(response.data.data);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Chargement des données d'une tâche par identifiant public
     * @param int publicId
     * @param bool forceLoad Vrai si on doit forcer le rechargement de la tâche
     * @return Promise
     */
    const loadRunningTaskByPublicId = ((publicId, forceLoad = false) => {

        const loadedTask = getRunningTaskByPublicId.value(publicId);
        if(! forceLoad && loadedTask !== null) {
            return Promise.resolve();
        }

        const path = '/api/me/tasks/running/:publicId'.replace(':publicId', publicId);

        return axios.get(path).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                updateRunningTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Démarrage de la tâche en paramètre
     * @param object task
     * @return Promise
     */
    const startRunningTask = ((task) => {

        const path = '/api/me/tasks/running/:publicId/start'.replace(':publicId', task.publicId);

        return axios.post(path).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                updateRunningTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Modifie une tâche dans le store
     * @param object task Données de la tâche à mettre à jour dans le store
     * @return Promise
     */
    const updateRunningTask = ((task) => {

        const currentTasks = runningTasks.value;

        const index = currentTasks.findIndex((taskFromState) => taskFromState.publicId === task.publicId);
        if(index !== -1) {
            runningTasks.value[index] = { ...runningTasks.value[index], ...task };
        } else {
            runningTasks.value.push(task);
        }

        // Mise à jour dans les tâches à venir
        const currentIncomingTasks = incomingRunningTasks.value;
        const incomingTaskIndex = (currentIncomingTasks !== null) ? currentIncomingTasks.findIndex((taskFromState) => taskFromState.publicId === task.publicId) : -1;
        if(incomingTaskIndex !== -1) {
            incomingRunningTasks.value[incomingTaskIndex] = { ...incomingRunningTasks.value[index], ...task };
        }

        // Mise à jour dans la liste
        const currentListData = runningTaskList.value.tasks || [];
        const indexFromList = currentListData.findIndex((taskFromState) => taskFromState.publicId === task.publicId);
        if(indexFromList !== -1) {
            const currentTaskFromList = runningTaskList.value.tasks[indexFromList];
            runningTaskList.value.tasks[indexFromList] = { ...currentTaskFromList, ...task };
        }

    });

    /**
     * Modifie la liste des tâches dans le store
     * @param data Données des tâches : total et items
     * @return void
     */
    const updateRunningTaskList = ((data) => {

        const currentData = runningTaskList.value;
        if(currentData.total === undefined) {
            runningTaskList.value = {
                total: data.total,
                tasks: data.items
            };

            data.items.forEach((task) => {
                updateRunningTask(task);
            });

        } else {
            runningTaskList.value.total = data.total;

            const currentTasks = runningTaskList.value.tasks;
            const newTasks = data.items.filter((item) => ! currentTasks.find((current) => current.id === item.id));
            runningTaskList.value.tasks = [ ...currentTasks, ...newTasks ];

            newTasks.forEach((task) => {
                updateRunningTask(task);
            });
        }

    });

    /**
     * Modifie la liste des tâches à venir dans le store
     * @param array tasks Tâches à mettre à jour
     * @return void
     */
    const updateIncomingRunningTasks = ((tasks) => {
        incomingRunningTasks.value = tasks;
        tasks.forEach((task) => {
            updateRunningTask(task);
        });
    });

    /**
     * Réinitialisation de la liste tâches
     * @return void
     */
    const resetRunningTaskList = (() => {
        runningTaskList.value = {};
    });

    /**
     * Réinitialisation de la liste tâches à venir
     * @return void
     */
    const resetIncomingTaskList = (() => {
        incomingRunningTasks.value = null;
    });

    return {
        getRunningTaskList,
        getRunningTaskByPublicId,
        getIncomingRunningTasks,
        loadRunningTaskList,
        loadRunningTaskByPublicId,
        loadIncomingRunningTasks,
        updateRunningTask,
        startRunningTask,
        resetIncomingTaskList
    };

}));


